import { useEffect, useState } from "react";

import api from "../api/client";
import { useSort, sortRows, SortableTh } from "../components/SortableTh";

const fmt = (cents: number) =>
  `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

type Buckets = {
  current: number;
  d1_30: number;
  d31_60: number;
  d61_90: number;
  d90_plus: number;
};
type CustomerRow = Buckets & { customer_id: number; customer_name: string; total_cents: number };
type InvoiceRow = {
  invoice_id: number;
  invoice_number: string | null;
  customer_name: string;
  due_date: string | null;
  days_overdue: number;
  total_cents: number;
  balance_cents: number;
  bucket: string;
};
type Aging = {
  as_of: string;
  total_outstanding_cents: number;
  buckets: Buckets;
  by_customer: CustomerRow[];
  invoices: InvoiceRow[];
};

const BUCKET_LABELS: [keyof Buckets, string][] = [
  ["current", "Current"],
  ["d1_30", "1–30 days"],
  ["d31_60", "31–60 days"],
  ["d61_90", "61–90 days"],
  ["d90_plus", "90+ days"],
];

const BUCKET_COLOR: Record<string, string> = {
  current: "text-gray-700 dark:text-gray-300",
  d1_30: "text-amber-600 dark:text-amber-400",
  d31_60: "text-orange-600 dark:text-orange-400",
  d61_90: "text-red-600 dark:text-red-400",
  d90_plus: "text-red-800 dark:text-red-300 font-bold",
};

export default function PSAArAging() {
  const [data, setData] = useState<Aging | null>(null);
  const [err, setErr] = useState("");
  const custSort = useSort("customer_name");
  const invSort = useSort("invoice_number");

  useEffect(() => {
    api
      .get("/api/v1/psa/ar/aging")
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Failed to load AR aging"));
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">PSA — AR aging</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Outstanding balance on every sent invoice, bucketed by days past due
          {data ? ` (as of ${data.as_of})` : ""}.
        </p>
      </div>

      {err && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300 px-4 py-2 rounded-lg text-sm">{err}</div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            <div className="bg-brand/10 rounded-xl p-4">
              <div className="text-xs uppercase text-gray-500 dark:text-gray-400">Outstanding</div>
              <div className="text-xl font-bold text-brand dark:text-brand-light">{fmt(data.total_outstanding_cents)}</div>
            </div>
            {BUCKET_LABELS.map(([key, label]) => (
              <div key={key} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-4">
                <div className="text-xs uppercase text-gray-500 dark:text-gray-400">{label}</div>
                <div className={`text-lg font-mono ${BUCKET_COLOR[key]}`}>{fmt(data.buckets[key])}</div>
              </div>
            ))}
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
            <div className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border-b border-gray-100 dark:border-gray-700">
              By customer
            </div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900 text-gray-600 dark:text-gray-400 text-xs">
                <tr>
                  <SortableTh label="Customer" sortKey="customer_name" sort={custSort.sort} onSort={custSort.toggle} />
                  {BUCKET_LABELS.map(([k, l]) => (
                    <SortableTh key={k} label={l} sortKey={k} sort={custSort.sort} onSort={custSort.toggle} align="right" />
                  ))}
                  <SortableTh label="Total" sortKey="total_cents" sort={custSort.sort} onSort={custSort.toggle} align="right" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700 font-mono text-xs">
                {sortRows(data.by_customer, custSort.sort, (c, key) => {
                  switch (key) {
                    case "customer_name":
                      return (c.customer_name || "").toLowerCase();
                    case "total_cents":
                      return c.total_cents ?? 0;
                    default:
                      return (c[key as keyof Buckets] as number) ?? 0;
                  }
                }).map((c) => (
                  <tr key={c.customer_id} className="text-gray-900 dark:text-gray-100">
                    <td className="px-3 py-2 font-sans">{c.customer_name}</td>
                    {BUCKET_LABELS.map(([k]) => (
                      <td key={k} className="text-right px-3 py-2">{c[k] ? fmt(c[k]) : "—"}</td>
                    ))}
                    <td className="text-right px-3 py-2 font-bold">{fmt(c.total_cents)}</td>
                  </tr>
                ))}
                {data.by_customer.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-3 py-6 text-center text-gray-500 dark:text-gray-400 italic font-sans">
                      Nothing outstanding — all sent invoices are paid. 🎉
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {data.invoices.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
              <div className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border-b border-gray-100 dark:border-gray-700">
                Outstanding invoices
              </div>
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-900 text-gray-600 dark:text-gray-400 text-xs">
                  <tr>
                    <SortableTh label="Invoice" sortKey="invoice_number" sort={invSort.sort} onSort={invSort.toggle} />
                    <SortableTh label="Customer" sortKey="customer_name" sort={invSort.sort} onSort={invSort.toggle} />
                    <SortableTh label="Due" sortKey="due_date" sort={invSort.sort} onSort={invSort.toggle} />
                    <SortableTh label="Days overdue" sortKey="days_overdue" sort={invSort.sort} onSort={invSort.toggle} align="right" />
                    <SortableTh label="Balance" sortKey="balance_cents" sort={invSort.sort} onSort={invSort.toggle} align="right" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700 text-xs">
                  {sortRows(data.invoices, invSort.sort, (inv, key) => {
                    switch (key) {
                      case "customer_name":
                        return (inv.customer_name || "").toLowerCase();
                      case "due_date":
                        return inv.due_date ? Date.parse(inv.due_date) : 0;
                      case "days_overdue":
                        return inv.days_overdue ?? 0;
                      case "balance_cents":
                        return inv.balance_cents ?? 0;
                      default:
                        return (inv.invoice_number || "").toLowerCase();
                    }
                  }).map((inv) => (
                    <tr key={inv.invoice_id} className="text-gray-900 dark:text-gray-100">
                      <td className="px-3 py-2 font-mono">{inv.invoice_number || "—"}</td>
                      <td className="px-3 py-2">{inv.customer_name}</td>
                      <td className="px-3 py-2">{inv.due_date || "—"}</td>
                      <td className={`px-3 py-2 text-right font-mono ${BUCKET_COLOR[inv.bucket]}`}>
                        {inv.days_overdue > 0 ? inv.days_overdue : "—"}
                      </td>
                      <td className="px-3 py-2 text-right font-mono">{fmt(inv.balance_cents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
